'use client';

import React, { useEffect, useRef, useState } from 'react';
import Icon from '@/components/ui/AppIcon';
import { Beef, Package, Tractor, Truck, Wheat } from 'lucide-react';
import { FEATURES, GRAIN_FEED_LABEL } from '@/lib/features';
import { GRAIN_FEED_GROUPS, GRAIN_FEED_UNITS, grainFeedTypes } from '@/lib/grain-feed';
import BuyerRequestSection from './BuyerRequestSection';
import { createClient } from '@/lib/supabase/client';
import { createListing, notifyListingSubmitted, type NewListingInput } from '@/lib/listings';
import {
  fetchListingAllowance,
  listingAllowanceLabel,
  listingLengthLabel,
  type ListingAllowance,
} from '@/lib/limits';
import { useAuth } from '@/contexts/AuthContext';

type ListingType = 'cattle' | 'sheep' | 'pigs' | 'horses' | 'goats' | 'poultry' | 'deer' | 'alpacas' | 'grain' | 'haulage' | 'machinery' | 'deadstock';

interface FormSection {
  type: ListingType;
  label: string;
  group: 'livestock' | 'crops' | 'haulage' | 'machinery' | 'deadstock';
}

const listingTypes: FormSection[] = [
  { type: 'cattle',    label: 'Cattle',                group: 'livestock' },
  { type: 'sheep',     label: 'Sheep',                 group: 'livestock' },
  { type: 'pigs',      label: 'Pigs',                  group: 'livestock' },
  { type: 'horses',    label: 'Horses & Ponies',       group: 'livestock' },
  { type: 'goats',     label: 'Goats',                 group: 'livestock' },
  { type: 'poultry',   label: 'Poultry',               group: 'livestock' },
  { type: 'deer',      label: 'Deer',                  group: 'livestock' },
  { type: 'alpacas',   label: 'Alpacas & Llamas',      group: 'livestock' },
  { type: 'grain',     label: GRAIN_FEED_LABEL,        group: 'crops' },
  { type: 'haulage',   label: 'Haulage Job',           group: 'haulage' },
  { type: 'machinery', label: 'Machinery',             group: 'machinery' },
  { type: 'deadstock', label: 'Deadstock',             group: 'deadstock' },
];

/**
 * Which listings.category each group of forms writes to.
 *
 * Grain, feed and bedding share the existing 'crops-feed' category, so the
 * sector page and the marketplace filters pick them up with no migration.
 */
const CATEGORY_FOR_GROUP: Record<FormSection['group'], string> = {
  livestock: 'livestock',
  crops: 'crops-feed',
  haulage: 'haulage',
  machinery: 'machinery',
  deadstock: 'deadstock',
};


const ukCounties = [
  'Bedfordshire','Berkshire','Bristol','Buckinghamshire','Cambridgeshire','Cheshire',
  'City of London','Cornwall','Cumbria','Derbyshire','Devon','Dorset','Durham',
  'East Riding of Yorkshire','East Sussex','Essex','Gloucestershire','Greater London',
  'Greater Manchester','Hampshire','Herefordshire','Hertfordshire','Isle of Wight',
  'Kent','Lancashire','Leicestershire','Lincolnshire','Merseyside','Norfolk',
  'North Yorkshire','Northamptonshire','Northumberland','Nottinghamshire','Oxfordshire',
  'Rutland','Shropshire','Somerset','South Yorkshire','Staffordshire','Suffolk',
  'Surrey','Tyne and Wear','Warwickshire','West Midlands','West Sussex',
  'West Yorkshire','Wiltshire','Worcestershire',
  'Aberdeenshire','Angus','Argyll and Bute','Clackmannanshire','Dumfries and Galloway',
  'Dundee','East Ayrshire','East Lothian','Edinburgh','Falkirk','Fife','Glasgow',
  'Highland','Inverclyde','Midlothian','Moray','North Ayrshire','North Lanarkshire',
  'Orkney Islands','Perth and Kinross','Renfrewshire','Scottish Borders','Shetland Islands',
  'South Ayrshire','South Lanarkshire','Stirling','West Dunbartonshire','West Lothian',
  'Western Isles',
  'Anglesey','Blaenau Gwent','Bridgend','Caerphilly','Cardiff','Carmarthenshire',
  'Ceredigion','Conwy','Denbighshire','Flintshire','Gwynedd','Merthyr Tydfil',
  'Monmouthshire','Neath Port Talbot','Newport','Pembrokeshire','Powys',
  'Rhondda Cynon Taf','Swansea','Torfaen','Vale of Glamorgan','Wrexham',
  'Antrim','Armagh','Down','Fermanagh','Londonderry','Tyrone',
];

const assuranceSchemes = [
  'None','Red Tractor','RSPCA Assured','Organic (Soil Association)','Quality Meat Scotland (QMS)',
  'Assured Beef Breeds (ABB)','FABBL','Farm Assured Welsh Livestock (FAWL)','Bord Bia',
  'Pasture for Life','Biodynamic (Demeter)','Other',
];

const cattleBreeds = [
  'Aberdeen Angus','Ayrshire','Belgian Blue','Belted Galloway','British Blonde',
  'British Charolais','British Limousin','British Simmental','British White',
  'Brown Swiss','Dexter','Friesian','Galloway','Hereford','Highland','Holstein',
  'Jersey','Longhorn','Luing','Murray Grey','Parthenaise','Piedmontese','Red Poll',
  'Romagnola','Salers','Shorthorn','South Devon','Speckle Park','Sussex','Welsh Black',
  'Whitebred Shorthorn','Cross / Commercial','Other',
];

const sheepBreeds = [
  'Beltex','Berrichon du Cher','Blackface','Blue Faced Leicester','Border Leicester',
  'Charollais','Cheviot','Clun Forest','Dorper','Dorset','Exmoor Horn','Hampshire Down',
  'Herdwick','Jacob','Kerry Hill','Lleyn','Lonk','Mule','North Country Cheviot',
  'Oxford Down','Poll Dorset','Romney','Rouge de l\'Ouest','Ryeland','Shropshire',
  'Soay','Southdown','Suffolk','Swaledale','Texel','Vendeen','Welsh Mountain',
  'Wensleydale','White Faced Woodland','Cross / Commercial','Other',
];

const pigBreeds = [
  'Berkshire','British Lop','British Saddleback','Duroc','Gloucester Old Spot',
  'Hampshire','Iron Age','Kune Kune','Large Black','Large White','Landrace',
  'Mangalitza','Middle White','Oxford Sandy and Black','Pietrain','Tamworth',
  'Welsh','Wild Boar Cross','Cross / Commercial','Other',
];

const horseBreeds = [
  'Clydesdale','Cleveland Bay','Dales Pony','Dartmoor Pony','Exmoor Pony',
  'Fell Pony','Hackney','Haflinger','Highland Pony','Irish Draught','Lusitano',
  'New Forest Pony','Percheron','Shetland Pony','Shire','Suffolk Punch',
  'Thoroughbred','Welsh Cob','Welsh Mountain Pony','Welsh Section B','Welsh Section C',
  'Warmblood','Cross','Other',
];

const goatBreeds = [
  'Anglo Nubian','Bagot','Boer','British Alpine','British Saanen','British Toggenburg',
  'Cashmere','Golden Guernsey','Kiko','Pygmy','Saanen','Toggenburg','Cross','Other',
];

const poultryTypes = [
  'Broiler Chickens','Laying Hens','Breeding Stock','Turkeys','Ducks','Geese',
  'Guinea Fowl','Quail','Pheasants','Partridge','Peacocks','Other',
];

const deerBreeds = [
  'Red Deer','Fallow Deer','Roe Deer','Sika Deer','Reindeer','Wapiti','Other',
];

const machineryCategories = [
  'Tractors','Combine Harvesters','Forage Harvesters','Balers (Round)','Balers (Square)',
  'Mowers / Conditioners','Tedders','Rakes','Sprayers (Trailed)','Sprayers (Self-Propelled)',
  'Fertiliser Spreaders','Seed Drills','Cultivators','Ploughs','Subsoilers',
  'Power Harrows','Disc Harrows','Trailers (Grain)','Trailers (Livestock)','Trailers (Flatbed)',
  'Telehandlers','Loaders','Diggers / Excavators','Slurry Tankers','Muck Spreaders',
  'Potato Harvesters','Root Harvesters','Irrigation Equipment','Grain Dryers',
  'Grain Augers','Conveyors','Fencing Equipment','Livestock Handling Equipment',
  'Sheep Handling','Cattle Crushes','Weighing Equipment','Milking Equipment',
  'Generators','Quad Bikes / ATVs','Utility Vehicles','Other',
];

const deadstockCategories = [
  'Wool Clip','Hides / Skins','Fallen Stock (Collection Required)','Feathers',
  'Antlers / Velvet','Bones / Offal','Manure / Slurry','Straw (Surplus)',
  'Hay (Surplus)','Silage (Surplus)','Other',
];

const haulageVehicleTypes = [
  'Single Deck Livestock Lorry','Double Deck Livestock Lorry','Cattle Truck',
  'Horse Box (Small — up to 3.5t)','Horse Box (Large — HGV)','Flatbed Lorry',
  'Low Loader','Curtainsider','Refrigerated Lorry','Tipper','Grain Lorry',
  'Tanker','Tractor Unit + Trailer','Other',
];

// ─── Sub-forms ───────────────────────────────────────────────────────────────

function FieldRow({ children }: { children: React.ReactNode }) {
  return <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">{children}</div>;
}

// "Number of Head" -> "number_of_head", "Price (£)" -> "price".
export function slugifyLabel(label: string) {
  return label
    .toLowerCase()
    .replace(/[^a-z0-9]+/g, '_')
    .replace(/^_+|_+$/g, '');
}

function FormField({ label, required, children, hint }: { label: string; required?: boolean; children: React.ReactNode; hint?: string }) {
  // Give every input a name derived from its label so the whole form can be read
  // back with FormData on submit. An explicit name on the input always wins.
  const named = React.isValidElement(children)
    ? (() => {
        const el = children as React.ReactElement<Record<string, unknown>>;
        const isFormControl = el.type === 'input' || el.type === 'select' || el.type === 'textarea';
        if (!isFormControl || el.props.name) return children;
        return React.cloneElement(el, { name: slugifyLabel(label) });
      })()
    : children;

  return (
    <div className="flex flex-col gap-1">
      <label className="text-sm font-semibold text-foreground">
        {label}{required && <span className="text-red-500 ml-0.5">*</span>}
      </label>
      {named}
      {hint && <p className="text-xs text-muted-foreground">{hint}</p>}
    </div>
  );
}

function SectionHeading({ title, iconName }: { title: string; iconName: string }) {
  return (
    <div className="flex items-center gap-2 pb-2 border-b border-border mb-4">
      <Icon name={iconName} size={18} className="text-primary shrink-0" />
      <h3 className="text-base font-bold text-foreground">{title}</h3>
    </div>
  );
}

// ─── Cattle Form ─────────────────────────────────────────────────────────────
function CattleForm() {
  return (
    <div className="space-y-6">
      <SectionHeading title="Cattle Details" iconName="Square3Stack3DIcon" />
      <FieldRow>
        <FormField label="Listing Title" required>
          <input className="form-input" placeholder="e.g. 12 Limousin x Heifers" />
        </FormField>
        <FormField label="Breed" required>
          <select className="form-input">
            <option value="">Select breed…</option>
            {cattleBreeds.map(b => <option key={b} value={b}>{b}</option>)}
          </select>
        </FormField>
      </FieldRow>
      <FieldRow>
        <FormField label="Category" required>
          <select className="form-input">
            <option value="">Select category…</option>
            {['Cows in Calf','Cows with Calves','Heifers in Calf','Heifers (Store)','Heifers (Finished)','Steers (Store)','Steers (Finished)','Bulls','Calves (Dairy)','Calves (Beef)','Cull Cows','Breeding Stock'].map(c => <option key={c}>{c}</option>)}
          </select>
        </FormField>
        <FormField label="Number of Head" required>
          <input className="form-input" type="number" min="1" placeholder="e.g. 12" />
        </FormField>
      </FieldRow>
      <FieldRow>
        <FormField label="Age / Age Range" required hint="e.g. 18–22 months">
          <input className="form-input" placeholder="e.g. 18–22 months" />
        </FormField>
        <FormField label="Average Live Weight (kg)" hint="Approximate average">
          <input className="form-input" type="number" placeholder="e.g. 380" />
        </FormField>
      </FieldRow>
      <FieldRow>
        <FormField label="Weight Range (kg)" hint="e.g. 360–420 kg">
          <input className="form-input" placeholder="e.g. 360–420 kg" />
        </FormField>
        <FormField label="Average Daily Gain (kg/day)" hint="If known">
          <input className="form-input" type="number" step="0.01" placeholder="e.g. 1.2" />
        </FormField>
      </FieldRow>
      <FieldRow>
        <FormField label="Sex">
          <select className="form-input">
            <option value="">Select…</option>
            {['Heifers','Steers','Bulls','Cows','Mixed'].map(s => <option key={s}>{s}</option>)}
          </select>
        </FormField>
        <FormField label="Pregnancy Status">
          <select className="form-input">
            <option value="">Not applicable</option>
            {['In Calf (Stage Unknown)','In Calf — Early','In Calf — Mid','In Calf — Late','With Calf at Foot','Not in Calf'].map(s => <option key={s}>{s}</option>)}
          </select>
        </FormField>
      </FieldRow>
      <FieldRow>
        <FormField label="Sire Breed (if cross)">
          <input className="form-input" placeholder="e.g. Limousin" />
        </FormField>
        <FormField label="Dam Breed (if cross)">
          <input className="form-input" placeholder="e.g. Friesian" />
        </FormField>
      </FieldRow>
      <SectionHeading title="Health & Assurance" iconName="ShieldCheckIcon" />
      <FieldRow>
        <FormField label="Assurance Scheme">
          <select className="form-input">
            {assuranceSchemes.map(s => <option key={s}>{s}</option>)}
          </select>
        </FormField>
        <FormField label="BVD Status">
          <select className="form-input">
            <option value="">Unknown</option>
            {['BVD Free (Tested)','BVD Negative (Not Tested)','BVD Vaccinated','BVD Positive — Not Applicable'].map(s => <option key={s}>{s}</option>)}
          </select>
        </FormField>
      </FieldRow>
      <FieldRow>
        <FormField label="TB Status" required>
          <select className="form-input">
            <option value="">Select…</option>
            {['TB Clear — Tested within 60 days','TB Clear — Annual Test','TB Restricted Holding','Pre-Movement Test Required','OTF (Officially Tuberculosis Free)'].map(s => <option key={s}>{s}</option>)}
          </select>
        </FormField>
        <FormField label="Vaccinations">
          <input className="form-input" placeholder="e.g. IBR, Leptospirosis, BVD" />
        </FormField>
      </FieldRow>
      <FormField label="Health History / Additional Notes">
        <textarea className="form-input min-h-[80px]" placeholder="Any relevant health history, treatments, or notes…" />
      </FormField>
      <SectionHeading title="Feeding & Management" iconName="ClipboardDocumentListIcon" />
      <FieldRow>
        <FormField label="Current Diet">
          <select className="form-input">
            <option value="">Select…</option>
            {['Grass Only','Grass + Silage','Silage + Concentrates','Housed on TMR','Creep Fed','Weaned — Grass','Weaned — Housed','Other'].map(s => <option key={s}>{s}</option>)}
          </select>
        </FormField>
        <FormField label="Housing Status">
          <select className="form-input">
            <option value="">Select…</option>
            {['Housed','Outwintered','Grazing','Yarded'].map(s => <option key={s}>{s}</option>)}
          </select>
        </FormField>
      </FieldRow>
      <FieldRow>
        <FormField label="Docility" hint="Important for buyer safety">
          <select className="form-input">
            <option value="">Select…</option>
            {['Very Quiet','Quiet','Average','Lively','Requires Care'].map(s => <option key={s}>{s}</option>)}
          </select>
        </FormField>
        <FormField label="Suitable For">
          <select className="form-input">
            <option value="">Select…</option>
            {['Breeding','Finishing','Dairy','Show','Store','Cull'].map(s => <option key={s}>{s}</option>)}
          </select>
        </FormField>
      </FieldRow>
    </div>
  );
}

// ─── Sheep Form ───────────────────────────────────────────────────────────────
function SheepForm() {
  return (
    <div className="space-y-6">
      <SectionHeading title="Sheep Details" iconName="Square3Stack3DIcon" />
      <FieldRow>
        <FormField label="Listing Title" required>
          <input className="form-input" placeholder="e.g. 40 Suffolk x Ewes in Lamb" />
        </FormField>
        <FormField label="Breed" required>
          <select className="form-input">
            <option value="">Select breed…</option>
            {sheepBreeds.map(b => <option key={b}>{b}</option>)}
          </select>
        </FormField>
      </FieldRow>
      <FieldRow>
        <FormField label="Category" required>
          <select className="form-input">
            <option value="">Select…</option>
            {['Ewes in Lamb','Ewes with Lambs','Ewe Lambs','Store Lambs','Finished Lambs','Breeding Rams','Shearlings (Ewes)','Shearlings (Rams)','Cull Ewes','Hoggs','Wether Lambs'].map(c => <option key={c}>{c}</option>)}
          </select>
        </FormField>
        <FormField label="Number of Head" required>
          <input className="form-input" type="number" min="1" placeholder="e.g. 40" />
        </FormField>
      </FieldRow>
      <FieldRow>
        <FormField label="Age / Age Range" required>
          <input className="form-input" placeholder="e.g. 2–4 years" />
        </FormField>
        <FormField label="Average Live Weight (kg)">
          <input className="form-input" type="number" placeholder="e.g. 70" />
        </FormField>
      </FieldRow>
      <FieldRow>
        <FormField label="Pregnancy Status">
          <select className="form-input">
            <option value="">Not applicable</option>
            {['In Lamb — Scanned Twins','In Lamb — Scanned Singles','In Lamb — Not Scanned','With Lambs at Foot','Barren','Not Tupped'].map(s => <option key={s}>{s}</option>)}
          </select>
        </FormField>
        <FormField label="Ram Breed Used">
          <input className="form-input" placeholder="e.g. Texel" />
        </FormField>
      </FieldRow>
      <FieldRow>
        <FormField label="Lambing Date (Expected)">
          <input className="form-input" type="date" />
        </FormField>
        <FormField label="Lambing Percentage (Previous)">
          <input className="form-input" placeholder="e.g. 175%" />
        </FormField>
      </FieldRow>
      <SectionHeading title="Health & Assurance" iconName="ShieldCheckIcon" />
      <FieldRow>
        <FormField label="Assurance Scheme">
          <select className="form-input">
            {assuranceSchemes.map(s => <option key={s}>{s}</option>)}
          </select>
        </FormField>
        <FormField label="Maedi Visna (MV) Status">
          <select className="form-input">
            <option value="">Unknown</option>
            {['MV Accredited','MV Negative (Tested)','Not Tested'].map(s => <option key={s}>{s}</option>)}
          </select>
        </FormField>
      </FieldRow>
      <FieldRow>
        <FormField label="Scrapie Status">
          <select className="form-input">
            <option value="">Unknown</option>
            {['NSP Monitored Flock','Genotyped — ARR/ARR','Genotyped — Other','Not Tested'].map(s => <option key={s}>{s}</option>)}
          </select>
        </FormField>
        <FormField label="Vaccinations">
          <input className="form-input" placeholder="e.g. Heptavac P, Footvax" />
        </FormField>
      </FieldRow>
      <FieldRow>
        <FormField label="Feet Condition">
          <select className="form-input">
            <option value="">Select…</option>
            {['Excellent','Good','Recently Treated','Footrot History'].map(s => <option key={s}>{s}</option>)}
          </select>
        </FormField>
        <FormField label="Worming Programme">
          <input className="form-input" placeholder="e.g. Quarterly, Zolvix" />
        </FormField>
      </FieldRow>
      <FormField label="Additional Notes">
        <textarea className="form-input min-h-[80px]" placeholder="Any relevant health history, management notes…" />
      </FormField>
    </div>
  );
}

// ─── Pigs Form ────────────────────────────────────────────────────────────────
function PigsForm() {
  return (
    <div className="space-y-6">
      <SectionHeading title="Pig Details" iconName="Square3Stack3DIcon" />
      <FieldRow>
        <FormField label="Listing Title" required>
          <input className="form-input" placeholder="e.g. 25 Large White x Weaners" />
        </FormField>
        <FormField label="Breed" required>
          <select className="form-input">
            <option value="">Select breed…</option>
            {pigBreeds.map(b => <option key={b}>{b}</option>)}
          </select>
        </FormField>
      </FieldRow>
      <FieldRow>
        <FormField label="Category" required>
          <select className="form-input">
            <option value="">Select…</option>
            {['Weaners','Stores','Baconers','Porkers','Gilts (Breeding)','Boars','Sows','In-Pig Gilts','In-Pig Sows','Cull Sows','Cull Boars'].map(c => <option key={c}>{c}</option>)}
          </select>
        </FormField>
        <FormField label="Number of Pigs" required>
          <input className="form-input" type="number" min="1" placeholder="e.g. 25" />
        </FormField>
      </FieldRow>
      <FieldRow>
        <FormField label="Age / Age Range" required>
          <input className="form-input" placeholder="e.g. 8 weeks" />
        </FormField>
        <FormField label="Average Live Weight (kg)">
          <input className="form-input" type="number" placeholder="e.g. 22" />
        </FormField>
      </FieldRow>
      <FieldRow>
        <FormField label="Sex">
          <select className="form-input">
            <option value="">Select…</option>
            {['Gilts','Boars','Castrates','Mixed'].map(s => <option key={s}>{s}</option>)}
          </select>
        </FormField>
        <FormField label="Production System">
          <select className="form-input">
            <option value="">Select…</option>
            {['Outdoor Bred','Outdoor Reared','Free Range','Indoor — Straw Based','Indoor — Slatted','Organic'].map(s => <option key={s}>{s}</option>)}
          </select>
        </FormField>
      </FieldRow>
      <SectionHeading title="Health & Assurance" iconName="ShieldCheckIcon" />
      <FieldRow>
        <FormField label="Assurance Scheme">
          <select className="form-input">
            {assuranceSchemes.map(s => <option key={s}>{s}</option>)}
          </select>
        </FormField>
        <FormField label="PRRS Status">
          <select className="form-input">
            <option value="">Unknown</option>
            {['PRRS Negative (Tested)','PRRS Vaccinated','PRRS Positive','Not Tested'].map(s => <option key={s}>{s}</option>)}
          </select>
        </FormField>
      </FieldRow>
      <FieldRow>
        <FormField label="Vaccinations">
          <input className="form-input" placeholder="e.g. Mycoplasma, Circovirus" />
        </FormField>
        <FormField label="Worming / Treatments">
          <input className="form-input" placeholder="e.g. Ivermectin, Flubenol" />
        </FormField>
      </FieldRow>
      <FormField label="Additional Notes">
        <textarea className="form-input min-h-[80px]" placeholder="Any relevant health history, management notes…" />
      </FormField>
    </div>
  );
}

// ─── Horses Form ─────────────────────────────────────────────────────────────
function HorsesForm() {
  return (
    <div className="space-y-6">
      <SectionHeading title="Horse / Pony Details" iconName="Square3Stack3DIcon" />
      <FieldRow>
        <FormField label="Listing Title" required>
          <input className="form-input" placeholder="e.g. 16.2hh Bay Warmblood Mare" />
        </FormField>
        <FormField label="Breed" required>
          <select className="form-input">
            <option value="">Select breed…</option>
            {horseBreeds.map(b => <option key={b}>{b}</option>)}
          </select>
        </FormField>
      </FieldRow>
      <FieldRow>
        <FormField label="Category">
          <select className="form-input">
            <option value="">Select…</option>
            {['Mare','Gelding','Stallion','Filly','Colt','Foal','Pony','Miniature Horse','Donkey','Mule'].map(c => <option key={c}>{c}</option>)}
          </select>
        </FormField>
        <FormField label="Age" required>
          <input className="form-input" placeholder="e.g. 7 years" />
        </FormField>
      </FieldRow>
      <FieldRow>
        <FormField label="Height (hh)">
          <input className="form-input" placeholder="e.g. 16.2" />
        </FormField>
        <FormField label="Colour">
          <select className="form-input">
            <option value="">Select…</option>
            {['Bay','Dark Bay','Brown','Black','Chestnut','Grey','Roan','Dun','Palomino','Skewbald','Piebald','Cremello','Appaloosa','Other'].map(c => <option key={c}>{c}</option>)}
          </select>
        </FormField>
      </FieldRow>
      <FieldRow>
        <FormField label="Passport / Microchip">
          <select className="form-input">
            <option value="">Select…</option>
            {['Passported & Microchipped','Passported Only','Microchipped Only','Neither — Foal','Other'].map(s => <option key={s}>{s}</option>)}
          </select>
        </FormField>
        <FormField label="Breeding / Stud Book">
          <input className="form-input" placeholder="e.g. BWBS, Weatherbys, SPSS" />
        </FormField>
      </FieldRow>
      <FieldRow>
        <FormField label="Discipline / Use">
          <select className="form-input">
            <option value="">Select…</option>
            {['Dressage','Show Jumping','Eventing','Showing','Hacking','Hunting','Driving','Breeding','Farm Work','Companion','Unbroken'].map(s => <option key={s}>{s}</option>)}
          </select>
        </FormField>
        <FormField label="Level / Experience">
          <input className="form-input" placeholder="e.g. Novice BE, BS Newcomers" />
        </FormField>
      </FieldRow>
      <SectionHeading title="Health & Vetting" iconName="ShieldCheckIcon" />
      <FieldRow>
        <FormField label="Vetting Status">
          <select className="form-input">
            <option value="">Select…</option>
            {['5-Stage Vetting Passed','2-Stage Vetting Passed','Not Vetted','Vetting Available on Request'].map(s => <option key={s}>{s}</option>)}
          </select>
        </FormField>
        <FormField label="Vaccinations">
          <select className="form-input">
            <option value="">Select…</option>
            {['Flu & Tetanus — Up to Date','Flu Only — Up to Date','Tetanus Only','Not Vaccinated','Unknown'].map(s => <option key={s}>{s}</option>)}
          </select>
        </FormField>
      </FieldRow>
      <FieldRow>
        <FormField label="Shoeing">
          <select className="form-input">
            <option value="">Select…</option>
            {['Shod All Round','Front Shoes Only','Barefoot','Booted'].map(s => <option key={s}>{s}</option>)}
          </select>
        </FormField>
        <FormField label="Dentistry (Last Check)">
          <input className="form-input" placeholder="e.g. 6 months ago" />
        </FormField>
      </FieldRow>
      <FormField label="Additional Notes / Quirks">
        <textarea className="form-input min-h-[80px]" placeholder="Any relevant health, behaviour, or management notes…" />
      </FormField>
    </div>
  );
}

// ─── Goats Form ───────────────────────────────────────────────────────────────
function GoatsForm() {
  return (
    <div className="space-y-6">
      <SectionHeading title="Goat Details" iconName="Square3Stack3DIcon" />
      <FieldRow>
        <FormField label="Listing Title" required>
          <input className="form-input" placeholder="e.g. 6 Boer x Wether Kids" />
        </FormField>
        <FormField label="Breed" required>
          <select className="form-input">
            <option value="">Select breed…</option>
            {goatBreeds.map(b => <option key={b}>{b}</option>)}
          </select>
        </FormField>
      </FieldRow>
      <FieldRow>
        <FormField label="Category" required>
          <select className="form-input">
            <option value="">Select…</option>
            {['Does (Milking)','Does (Dry)','In-Kid Does','Kids (Male)','Kids (Female)','Wethers','Bucks','Cull Does'].map(c => <option key={c}>{c}</option>)}
          </select>
        </FormField>
        <FormField label="Number of Goats" required>
          <input className="form-input" type="number" min="1" placeholder="e.g. 6" />
        </FormField>
      </FieldRow>
      <FieldRow>
        <FormField label="Age / Age Range" required>
          <input className="form-input" placeholder="e.g. 3 months" />
        </FormField>
        <FormField label="Average Live Weight (kg)">
          <input className="form-input" type="number" placeholder="e.g. 25" />
        </FormField>
      </FieldRow>
      <FieldRow>
        <FormField label="Milk Yield (if dairy)" hint="Litres per day average">
          <input className="form-input" placeholder="e.g. 3.5 litres/day" />
        </FormField>
        <FormField label="Assurance Scheme">
          <select className="form-input">
            {assuranceSchemes.map(s => <option key={s}>{s}</option>)}
          </select>
        </FormField>
      </FieldRow>
      <FieldRow>
        <FormField label="CAE Status">
          <select className="form-input">
            <option value="">Unknown</option>
            {['CAE Negative (Tested)','CAE Vaccinated','Not Tested'].map(s => <option key={s}>{s}</option>)}
          </select>
        </FormField>
        <FormField label="Vaccinations">
          <input className="form-input" placeholder="e.g. Heptavac P" />
        </FormField>
      </FieldRow>
      <FormField label="Additional Notes">
        <textarea className="form-input min-h-[80px]" placeholder="Any relevant health history, management notes…" />
      </FormField>
    </div>
  );
}

// ─── Poultry Form ─────────────────────────────────────────────────────────────
function PoultryForm() {
  return (
    <div className="space-y-6">
      <SectionHeading title="Poultry Details" iconName="Square3Stack3DIcon" />
      <FieldRow>
        <FormField label="Listing Title" required>
          <input className="form-input" placeholder="e.g. 500 Free Range Laying Hens" />
        </FormField>
        <FormField label="Type" required>
          <select className="form-input">
            <option value="">Select type…</option>
            {poultryTypes.map(t => <option key={t}>{t}</option>)}
          </select>
        </FormField>
      </FieldRow>
      <FieldRow>
        <FormField label="Breed / Strain">
          <input className="form-input" placeholder="e.g. Lohmann Brown, Ross 308" />
        </FormField>
        <FormField label="Number of Birds" required>
          <input className="form-input" type="number" min="1" placeholder="e.g. 500" />
        </FormField>
      </FieldRow>
      <FieldRow>
        <FormField label="Age (weeks)" required>
          <input className="form-input" type="number" placeholder="e.g. 72" />
        </FormField>
        <FormField label="Production System">
          <select className="form-input">
            <option value="">Select…</option>
            {['Free Range','Organic','Barn','Colony Cage','Enriched Cage','Backyard / Small Flock'].map(s => <option key={s}>{s}</option>)}
          </select>
        </FormField>
      </FieldRow>
      <FieldRow>
        <FormField label="Current Lay Rate (%)" hint="If laying hens">
          <input className="form-input" type="number" min="0" max="100" placeholder="e.g. 85" />
        </FormField>
        <FormField label="Assurance Scheme">
          <select className="form-input">
            {assuranceSchemes.map(s => <option key={s}>{s}</option>)}
          </select>
        </FormField>
      </FieldRow>
      <FieldRow>
        <FormField label="Vaccination Programme">
          <input className="form-input" placeholder="e.g. Marek's, IB, ND, IBD" />
        </FormField>
        <FormField label="Salmonella Status">
          <select className="form-input">
            <option value="">Unknown</option>
            {['Negative (Tested)','Vaccinated','Not Tested'].map(s => <option key={s}>{s}</option>)}
          </select>
        </FormField>
      </FieldRow>
      <FormField label="Additional Notes">
        <textarea className="form-input min-h-[80px]" placeholder="Any relevant health history, management notes…" />
      </FormField>
    </div>
  );
}

// ─── Deer Form ────────────────────────────────────────────────────────────────
function DeerForm() {
  return (
    <div className="space-y-6">
      <SectionHeading title="Deer Details" iconName="Square3Stack3DIcon" />
      <FieldRow>
        <FormField label="Listing Title" required>
          <input className="form-input" placeholder="e.g. 10 Red Deer Hinds" />
        </FormField>
        <FormField label="Species / Breed" required>
          <select className="form-input">
            <option value="">Select…</option>
            {deerBreeds.map(b => <option key={b}>{b}</option>)}
          </select>
        </FormField>
      </FieldRow>
      <FieldRow>
        <FormField label="Category" required>
          <select className="form-input">
            <option value="">Select…</option>
            {['Hinds','Stags','Calves','Yearlings','In-Calf Hinds','Breeding Stags','Velvet Stags','Cull Animals'].map(c => <option key={c}>{c}</option>)}
          </select>
        </FormField>
        <FormField label="Number of Head" required>
          <input className="form-input" type="number" min="1" placeholder="e.g. 10" />
        </FormField>
      </FieldRow>
      <FieldRow>
        <FormField label="Age / Age Range" required>
          <input className="form-input" placeholder="e.g. 2–4 years" />
        </FormField>
        <FormField label="Average Live Weight (kg)">
          <input className="form-input" type="number" placeholder="e.g. 90" />
        </FormField>
      </FieldRow>
      <FieldRow>
        <FormField label="Purpose">
          <select className="form-input">
            <option value="">Select…</option>
            {['Venison Production','Velvet Production','Breeding','Park Deer','Conservation'].map(s => <option key={s}>{s}</option>)}
          </select>
        </FormField>
        <FormField label="Tuberculosis Status">
          <select className="form-input">
            <option value="">Select…</option>
            {['TB Clear — Tested','TB Restricted','Pre-Movement Test Required'].map(s => <option key={s}>{s}</option>)}
          </select>
        </FormField>
      </FieldRow>
      <FormField label="Additional Notes">
        <textarea className="form-input min-h-[80px]" placeholder="Any relevant health history, management notes…" />
      </FormField>
    </div>
  );
}

// ─── Alpacas Form ─────────────────────────────────────────────────────────────
function AlpacasForm() {
  return (
    <div className="space-y-6">
      <SectionHeading title="Alpaca / Llama Details" iconName="Square3Stack3DIcon" />
      <FieldRow>
        <FormField label="Listing Title" required>
          <input className="form-input" placeholder="e.g. 3 Huacaya Alpaca Females" />
        </FormField>
        <FormField label="Species">
          <select className="form-input">
            <option value="">Select…</option>
            {['Huacaya Alpaca','Suri Alpaca','Llama','Guanaco','Vicuña'].map(s => <option key={s}>{s}</option>)}
          </select>
        </FormField>
      </FieldRow>
      <FieldRow>
        <FormField label="Category" required>
          <select className="form-input">
            <option value="">Select…</option>
            {['Females (Hembras)','Males (Machos)','Crias','Weanlings','Stud Males','Pregnant Females','Geldings'].map(c => <option key={c}>{c}</option>)}
          </select>
        </FormField>
        <FormField label="Number" required>
          <input className="form-input" type="number" min="1" placeholder="e.g. 3" />
        </FormField>
      </FieldRow>
      <FieldRow>
        <FormField label="Age / Age Range" required>
          <input className="form-input" placeholder="e.g. 2 years" />
        </FormField>
        <FormField label="Fibre Colour">
          <input className="form-input" placeholder="e.g. White, Fawn, Brown, Black" />
        </FormField>
      </FieldRow>
      <FieldRow>
        <FormField label="Registered (BAS / UKBAS)">
          <select className="form-input">
            <option value="">Select…</option>
            {['BAS Registered','UKBAS Registered','Both','Not Registered'].map(s => <option key={s}>{s}</option>)}
          </select>
        </FormField>
        <FormField label="Vaccinations">
          <input className="form-input" placeholder="e.g. Heptavac P, Clostridial" />
        </FormField>
      </FieldRow>
      <FormField label="Additional Notes">
        <textarea className="form-input min-h-[80px]" placeholder="Any relevant health history, management notes…" />
      </FormField>
    </div>
  );
}

// ─── Haulage Form ─────────────────────────────────────────────────────────────
function HaulageForm() {
  return (
    <div className="space-y-6">
      <SectionHeading title="Haulage Job Details" iconName="TruckIcon" />
      <FieldRow>
        <FormField label="Job Title" required>
          <input className="form-input" placeholder="e.g. 40 Ewes — Skipton to Ludlow" />
        </FormField>
        <FormField label="Vehicle Type Required" required>
          <select className="form-input">
            <option value="">Select…</option>
            {haulageVehicleTypes.map(v => <option key={v}>{v}</option>)}
          </select>
        </FormField>
      </FieldRow>
      <FieldRow>
        <FormField label="Collection Postcode / Town" required>
          <input className="form-input" placeholder="e.g. BD23 or Skipton" />
        </FormField>
        <FormField label="Collection County" required>
          <select className="form-input">
            <option value="">Select county…</option>
            {ukCounties.map(c => <option key={c}>{c}</option>)}
          </select>
        </FormField>
      </FieldRow>
      <FieldRow>
        <FormField label="Delivery Postcode / Town" required>
          <input className="form-input" placeholder="e.g. SY8 or Ludlow" />
        </FormField>
        <FormField label="Delivery County" required>
          <select className="form-input">
            <option value="">Select county…</option>
            {ukCounties.map(c => <option key={c}>{c}</option>)}
          </select>
        </FormField>
      </FieldRow>
      <FieldRow>
        <FormField label="Collection Date" required>
          <input className="form-input" type="date" />
        </FormField>
        <FormField label="Collection Time (Preferred)">
          <input className="form-input" type="time" />
        </FormField>
      </FieldRow>
      <FieldRow>
        <FormField label="Livestock Species">
          <select className="form-input">
            <option value="">Select…</option>
            {['Cattle','Sheep','Pigs','Horses','Goats','Poultry','Deer','Alpacas','Mixed','Not Livestock'].map(s => <option key={s}>{s}</option>)}
          </select>
        </FormField>
        <FormField label="Number of Animals / Load Size">
          <input className="form-input" placeholder="e.g. 40 ewes or 1 bull" />
        </FormField>
      </FieldRow>
      <FieldRow>
        <FormField label="Estimated Total Weight (kg)">
          <input className="form-input" type="number" placeholder="e.g. 2800" />
        </FormField>
        <FormField label="Budget / Max Rate (£)" hint="Leave blank to receive open bids">
          <input className="form-input" type="number" placeholder="e.g. 350" />
        </FormField>
      </FieldRow>
      <FieldRow>
        <FormField label="Movement Licence Required">
          <select className="form-input">
            <option value="">Select…</option>
            {['Yes — I will arrange','Yes — Haulier to arrange','Not Required','Unsure'].map(s => <option key={s}>{s}</option>)}
          </select>
        </FormField>
        <FormField label="Loading Facilities">
          <select className="form-input">
            <option value="">Select…</option>
            {['Loading Ramp Available','Crush / Race Available','Open Field — No Facilities','Other'].map(s => <option key={s}>{s}</option>)}
          </select>
        </FormField>
      </FieldRow>
      <FormField label="Special Requirements / Notes">
        <textarea className="form-input min-h-[80px]" placeholder="Any special requirements, access restrictions, or additional notes…" />
      </FormField>
    </div>
  );
}

// ─── Machinery Form ───────────────────────────────────────────────────────────
function MachineryForm() {
  return (
    <div className="space-y-6">
      <SectionHeading title="Machinery Details" iconName="WrenchScrewdriverIcon" />
      <FieldRow>
        <FormField label="Listing Title" required>
          <input className="form-input" placeholder="e.g. John Deere 6130R Tractor 2019" />
        </FormField>
        <FormField label="Category" required>
          <select className="form-input">
            <option value="">Select category…</option>
            {machineryCategories.map(c => <option key={c}>{c}</option>)}
          </select>
        </FormField>
      </FieldRow>
      <FieldRow>
        <FormField label="Make / Manufacturer" required>
          <input className="form-input" placeholder="e.g. John Deere, Claas, New Holland" />
        </FormField>
        <FormField label="Model" required>
          <input className="form-input" placeholder="e.g. 6130R, Lexion 770" />
        </FormField>
      </FieldRow>
      <FieldRow>
        <FormField label="Year of Manufacture" required>
          <input className="form-input" type="number" min="1950" max="2026" placeholder="e.g. 2019" />
        </FormField>
        <FormField label="Serial / Chassis Number" hint="Optional — helps buyers verify">
          <input className="form-input" placeholder="e.g. 1RW6130RJKL123456" />
        </FormField>
      </FieldRow>
      <FieldRow>
        <FormField label="Hours (Engine / Working)" hint="For tractors, combines etc.">
          <input className="form-input" type="number" placeholder="e.g. 4200" />
        </FormField>
        <FormField label="Acreage (if applicable)" hint="For drills, sprayers etc.">
          <input className="form-input" type="number" placeholder="e.g. 12000" />
        </FormField>
      </FieldRow>
      <FieldRow>
        <FormField label="Engine Power (hp / kW)">
          <input className="form-input" placeholder="e.g. 130hp / 97kW" />
        </FormField>
        <FormField label="Working Width (m)" hint="For implements">
          <input className="form-input" placeholder="e.g. 6m" />
        </FormField>
      </FieldRow>
      <FieldRow>
        <FormField label="Drive Type">
          <select className="form-input">
            <option value="">Select…</option>
            {['2WD','4WD / MFWD','Full Track','Half Track','Not Applicable'].map(s => <option key={s}>{s}</option>)}
          </select>
        </FormField>
        <FormField label="Transmission">
          <select className="form-input">
            <option value="">Select…</option>
            {['Manual','Semi-Powershift','Full Powershift','CVT / IVT','Hydrostatic','Not Applicable'].map(s => <option key={s}>{s}</option>)}
          </select>
        </FormField>
      </FieldRow>
      <SectionHeading title="Condition & Service History" iconName="ClipboardDocumentCheckIcon" />
      <FieldRow>
        <FormField label="Overall Condition" required>
          <select className="form-input">
            <option value="">Select…</option>
            {['Excellent — As New','Very Good','Good — Minor Wear','Fair — Some Repairs Needed','For Parts / Restoration'].map(s => <option key={s}>{s}</option>)}
          </select>
        </FormField>
        <FormField label="Service History">
          <select className="form-input">
            <option value="">Select…</option>
            {['Full Dealer Service History','Partial Service History','Owner Serviced — Records Available','No Records'].map(s => <option key={s}>{s}</option>)}
          </select>
        </FormField>
      </FieldRow>
      <FieldRow>
        <FormField label="Last Service Date">
          <input className="form-input" type="date" />
        </FormField>
        <FormField label="MOT / Test Certificate" hint="If applicable (e.g. trailers)">
          <select className="form-input">
            <option value="">Not Applicable</option>
            {['Valid MOT / Test Certificate','Expired — Needs Test','Not Required'].map(s => <option key={s}>{s}</option>)}
          </select>
        </FormField>
      </FieldRow>
      <FormField label="Specification / Options / Attachments">
        <textarea className="form-input min-h-[80px]" placeholder="List key specs, attachments, or options included (e.g. front linkage, GPS, loader, bucket)…" />
      </FormField>
      <FormField label="Known Faults / Defects">
        <textarea className="form-input min-h-[60px]" placeholder="Please disclose any known faults or defects honestly…" />
      </FormField>
    </div>
  );
}

// ─── Deadstock Form ───────────────────────────────────────────────────────────
function DeadstockForm() {
  return (
    <div className="space-y-6">
      <SectionHeading title="Deadstock Details" iconName="ArchiveBoxIcon" />
      <FieldRow>
        <FormField label="Listing Title" required>
          <input className="form-input" placeholder="e.g. Wool Clip — 400 Mule Fleeces" />
        </FormField>
        <FormField label="Category" required>
          <select className="form-input">
            <option value="">Select category…</option>
            {deadstockCategories.map(c => <option key={c}>{c}</option>)}
          </select>
        </FormField>
      </FieldRow>
      <FieldRow>
        <FormField label="Quantity / Volume" required>
          <input className="form-input" placeholder="e.g. 400 fleeces, 2 carcasses, 5 tonnes" />
        </FormField>
        <FormField label="Estimated Total Weight (kg)">
          <input className="form-input" type="number" placeholder="e.g. 600" />
        </FormField>
      </FieldRow>
      <FieldRow>
        <FormField label="Species (if applicable)">
          <select className="form-input">
            <option value="">Select…</option>
            {['Cattle','Sheep','Pigs','Horses','Goats','Poultry','Deer','Alpacas','Mixed','Not Applicable'].map(s => <option key={s}>{s}</option>)}
          </select>
        </FormField>
        <FormField label="Breed (if applicable)">
          <input className="form-input" placeholder="e.g. Mule, Hereford" />
        </FormField>
      </FieldRow>
      <FieldRow>
        <FormField label="Collection / Delivery">
          <select className="form-input">
            <option value="">Select…</option>
            {['Collection Only','Delivery Available (Cost TBC)','Delivery Included','Buyer Arranges Collection'].map(s => <option key={s}>{s}</option>)}
          </select>
        </FormField>
        <FormField label="Availability Date">
          <input className="form-input" type="date" />
        </FormField>
      </FieldRow>
      <FormField label="Description / Additional Notes">
        <textarea className="form-input min-h-[80px]" placeholder="Describe the product, condition, storage, or any other relevant details…" />
      </FormField>
    </div>
  );
}

// ─── Grain, Feed & Bedding Form ──────────────────────────────────────────────
//
// Two dropdowns rather than one long list: pick the group (Grain, Forage,
// Bedding…) and the second box narrows to the specific types in it. Both come
// from src/lib/grain-feed.ts, so a new heading appears here and on the browse
// page at the same time.
function GrainFeedForm() {
  const [group, setGroup] = useState('');
  const types = grainFeedTypes(group);

  return (
    <div className="space-y-6">
      <SectionHeading title={`${GRAIN_FEED_LABEL} Details`} iconName="GlobeEuropeAfricaIcon" />
      <FormField label="Listing Title" required>
        <input className="form-input" placeholder="e.g. 300t Feed Barley, off the combine" />
      </FormField>
      <FieldRow>
        <FormField label="Category" required>
          <select
            className="form-input"
            name="category"
            value={group}
            onChange={(e) => setGroup(e.target.value)}
            required
          >
            <option value="">Select category…</option>
            {GRAIN_FEED_GROUPS.map((g) => (
              <option key={g.value} value={g.value}>{g.label}</option>
            ))}
          </select>
        </FormField>
        {/* Named explicitly: a plain "type" collides with COLUMN_FOR_SLUG's
            sub_category mapping, which "category" has already filled — the
            specific heading would have been silently dropped on submit. */}
        <FormField label="Type" required>
          <select className="form-input" name="grain_feed_type" disabled={!group} required>
            <option value="">{group ? 'Select type…' : 'Choose a category first'}</option>
            {types.map((t) => <option key={t}>{t}</option>)}
          </select>
        </FormField>
      </FieldRow>
      <FieldRow>
        <FormField label="Quantity" required hint="Just the number — pick the unit alongside">
          <input className="form-input" name="quantity_volume" type="number" min="0" step="0.01" placeholder="e.g. 300" />
        </FormField>
        <FormField label="Unit" required>
          <select className="form-input">
            <option value="">Select unit…</option>
            {GRAIN_FEED_UNITS.map((u) => <option key={u}>{u}</option>)}
          </select>
        </FormField>
      </FieldRow>
      <FieldRow>
        <FormField label="Harvest / Production Year">
          <input className="form-input" type="number" min="1990" max="2100" placeholder="e.g. 2026" />
        </FormField>
        <FormField label="Storage">
          <select className="form-input">
            <option value="">Select…</option>
            {['Under Cover / Barn','Outside Sheeted','Outside Unsheeted','Clamp','Silo / Bin','Standing in Field'].map(s => <option key={s}>{s}</option>)}
          </select>
        </FormField>
      </FieldRow>
      <FieldRow>
        <FormField label="Moisture / Dry Matter (%)" hint="Where it matters — grain moisture, forage DM">
          <input className="form-input" placeholder="e.g. 15% moisture" />
        </FormField>
        <FormField label="Analysis Available?">
          <select className="form-input">
            <option value="">Select…</option>
            {['Yes — available on request','Yes — attached in photos','No analysis done'].map(s => <option key={s}>{s}</option>)}
          </select>
        </FormField>
      </FieldRow>
      <FieldRow>
        <FormField label="Bale Size / Packaging">
          <input className="form-input" placeholder="e.g. 4ft round, 120x90 square, 600kg bulk bags" />
        </FormField>
        <FormField label="Assurance Scheme">
          <select className="form-input">
            <option value="">Select…</option>
            {['None','Red Tractor','SQC (Scottish Quality Crops)','Organic — Soil Association','Organic — Other','TASCC','Other'].map(s => <option key={s}>{s}</option>)}
          </select>
        </FormField>
      </FieldRow>
      <FieldRow>
        <FormField label="Collection / Delivery">
          <select className="form-input">
            <option value="">Select…</option>
            {['Collection Only','Ex-Farm — Buyer Haulage','Delivery Available (Cost TBC)','Delivery Included','Delivered Price Quoted'].map(s => <option key={s}>{s}</option>)}
          </select>
        </FormField>
        <FormField label="Available From">
          <input className="form-input" type="date" />
        </FormField>
      </FieldRow>
      <FormField label="Description / Additional Notes">
        <textarea className="form-input min-h-[80px]" placeholder="Variety, treatment, where it was grown, weighbridge tickets, minimum load, anything a buyer would ask…" />
      </FormField>
    </div>
  );
}

// ─── Shared Location & Pricing Section ───────────────────────────────────────
function LocationPricingSection({
  showHaulageFields,
  bulkPricing,
}: {
  showHaulageFields?: boolean;
  /** Grain and forage are sold by the tonne or the bale, not per head. */
  bulkPricing?: boolean;
}) {
  const [priceType, setPriceType] = useState<'fixed' | 'poa' | 'perhead' | 'perkg' | 'pertonne' | 'perbale'>('fixed');

  return (
    <div className="space-y-6">
      {!showHaulageFields && (
        <>
          <SectionHeading title="Pricing" iconName="BanknotesIcon" />
          {/* Carries the chosen pricing basis through to FormData on submit. */}
          <input type="hidden" name="price_type" value={priceType} />
          <div className="flex flex-wrap gap-2 mb-4">
            {(bulkPricing
              ? [
                  { value: 'fixed', label: 'Fixed Price (whole lot)' },
                  { value: 'poa', label: 'Price on Application' },
                  { value: 'pertonne', label: 'Per Tonne' },
                  { value: 'perbale', label: 'Per Bale' },
                ]
              : [
                  { value: 'fixed', label: 'Fixed Price' },
                  { value: 'poa', label: 'Price on Application' },
                  { value: 'perhead', label: 'Per Head' },
                  { value: 'perkg', label: 'Per kg LW' },
                ]
            ).map(opt => (
              <button
                key={opt.value}
                type="button"
                onClick={() => setPriceType(opt.value as typeof priceType)}
                className={`px-4 py-2 rounded-lg text-sm font-semibold border transition-all ${
                  priceType === opt.value
                    ? 'bg-primary text-primary-foreground border-primary'
                    : 'bg-card text-foreground border-border hover:border-primary'
                }`}
              >
                {opt.label}
              </button>
            ))}
          </div>
          {priceType !== 'poa' && (
            <FieldRow>
              <FormField
                label={
                  priceType === 'perkg' ? 'Price per kg LW (£)'
                  : priceType === 'perhead' ? 'Price per Head (£)'
                  : priceType === 'pertonne' ? 'Price per Tonne (£)'
                  : priceType === 'perbale' ? 'Price per Bale (£)'
                  : 'Price (£)'
                }
                required
              >
                <input className="form-input" name="price" type="number" min="0" step="0.01" placeholder="e.g. 1450.00" />
              </FormField>
              <FormField label="VAT">
                <select className="form-input">
                  <option value="">Select…</option>
                  {['Price Includes VAT','Price Excludes VAT (+ VAT)','Not VAT Registered','Zero Rated'].map(s => <option key={s}>{s}</option>)}
                </select>
              </FormField>
            </FieldRow>
          )}
          <FormField label="Negotiable?">
            <div className="flex gap-4 mt-1">
              {['Yes','No','Offers Considered'].map(opt => (
                <label key={opt} className="flex items-center gap-2 cursor-pointer py-1">
                  <input type="radio" name="negotiable" value={opt} className="w-5 h-5 accent-primary shrink-0" />
                  <span className="text-sm text-foreground">{opt}</span>
                </label>
              ))}
            </div>
          </FormField>
        </>
      )}

      <SectionHeading title="Location" iconName="MapPinIcon" />
      <FieldRow>
        <FormField label="Town / Village" required>
          <input className="form-input" placeholder="e.g. Skipton" />
        </FormField>
        <FormField label="County" required>
          <select className="form-input">
            <option value="">Select county…</option>
            {ukCounties.map(c => <option key={c}>{c}</option>)}
          </select>
        </FormField>
      </FieldRow>
      <FieldRow>
        <FormField label="Postcode" hint="First part only, e.g. BD23">
          <input className="form-input" placeholder="e.g. BD23" />
        </FormField>
        {/* Held back until CPH/APHA verification is in place — src/lib/features.ts */}
        {FEATURES.cphVerification && (
          <FormField label="CPH Number" hint="County Parish Holding — required for livestock">
            <input className="form-input" placeholder="e.g. 12/345/6789" />
          </FormField>
        )}
      </FieldRow>

      <SectionHeading title="Contact & Visibility" iconName="PhoneIcon" />
      <FieldRow>
        <FormField label="Contact Name" required>
          <input className="form-input" placeholder="e.g. John Smith" />
        </FormField>
        <FormField label="Contact Phone" required>
          <input className="form-input" type="tel" placeholder="e.g. 07700 900123" />
        </FormField>
      </FieldRow>
      <FieldRow>
        <FormField label="Contact Email">
          <input className="form-input" type="email" placeholder="e.g. john@farmname.co.uk" />
        </FormField>
        <FormField label="Preferred Contact Method">
          <select className="form-input">
            <option value="">Select…</option>
            {['Phone','Email','WhatsApp','Any'].map(s => <option key={s}>{s}</option>)}
          </select>
        </FormField>
      </FieldRow>
      {/* No public/members-only choice here.
          Every approved listing is visible to every visitor — that is how the
          site is found. What membership unlocks is the seller's contact details
          and messaging, which is enforced by reveal_contact(), not by anything
          the seller picks on this form. The old radio buttons implied a privacy
          setting that did not exist. */}
      <div className="flex items-start gap-2.5 bg-slate-50 border border-slate-200 rounded-xl px-4 py-3">
        <Icon name="EyeIcon" size={17} className="text-slate-500 shrink-0 mt-0.5" />
        <p className="text-xs text-slate-700 leading-relaxed">
          Your listing is visible to everyone who visits FarmTradeDirect, so buyers can find it.
          Your contact details are not: only signed-in members can reveal them or message you.
        </p>
      </div>
    </div>
  );
}

// ─── Photos Section ───────────────────────────────────────────────────────────
const MAX_PHOTOS = 10;
const MAX_PHOTO_BYTES = 10 * 1024 * 1024;

function PhotosSection({
  photos,
  onChange,
}: {
  photos: File[];
  onChange: (files: File[]) => void;
}) {
  const [previews, setPreviews] = useState<string[]>([]);
  const [rejected, setRejected] = useState<string[]>([]);

  // Object URLs must be revoked or the browser holds the files in memory.
  useEffect(() => {
    const urls = photos.map((f) => URL.createObjectURL(f));
    setPreviews(urls);
    return () => urls.forEach((u) => URL.revokeObjectURL(u));
  }, [photos]);

  const addFiles = (incoming: FileList | null) => {
    if (!incoming?.length) return;
    const tooBig: string[] = [];
    const accepted = Array.from(incoming).filter((f) => {
      if (f.size > MAX_PHOTO_BYTES) {
        tooBig.push(f.name);
        return false;
      }
      return true;
    });
    setRejected(tooBig);
    onChange([...photos, ...accepted].slice(0, MAX_PHOTOS));
  };

  const removeAt = (idx: number) => onChange(photos.filter((_, i) => i !== idx));

  // Always show at least four slots, growing as photos are added.
  const slotCount = Math.min(MAX_PHOTOS, Math.max(4, photos.length + 1));

  return (
    <div className="space-y-4">
      <SectionHeading title="Photos & Media" iconName="PhotoIcon" />
      <p className="text-sm text-muted-foreground">Upload up to {MAX_PHOTOS} photos. First photo will be the main listing image. Listings with photos receive 3× more enquiries.</p>
      <div className="grid grid-cols-2 sm:grid-cols-4 gap-3">
        {Array.from({ length: slotCount }).map((_, i) => {
          const file = photos[i];
          if (file) {
            return (
              <div
                key={`photo-${i}-${file.name}`}
                className="relative aspect-square rounded-xl overflow-hidden border border-border bg-muted/30 group"
              >
                {/* eslint-disable-next-line @next/next/no-img-element */}
                <img src={previews[i]} alt={file.name} className="w-full h-full object-cover" />
                <button
                  type="button"
                  onClick={() => removeAt(i)}
                  aria-label={`Remove ${file.name}`}
                  className="absolute top-1.5 right-1.5 w-7 h-7 rounded-full bg-black/60 hover:bg-red-600 text-white flex items-center justify-center transition-colors"
                >
                  <Icon name="XMarkIcon" size={16} />
                </button>
                {i === 0 && (
                  <span className="absolute bottom-0 inset-x-0 bg-primary/90 text-primary-foreground text-[10px] font-bold text-center py-1">
                    MAIN PHOTO
                  </span>
                )}
              </div>
            );
          }
          return (
            <label
              key={`photo-slot-${i}`}
              className="aspect-square rounded-xl border-2 border-dashed border-border hover:border-primary transition-colors cursor-pointer flex flex-col items-center justify-center gap-2 bg-muted/30"
            >
              <Icon name="PhotoIcon" size={24} className="text-muted-foreground" />
              <span className="text-xs text-muted-foreground">{i === 0 ? 'Main Photo' : `Photo ${i + 1}`}</span>
              <input
                type="file"
                accept="image/*"
                multiple
                className="hidden"
                onChange={(e) => {
                  addFiles(e.target.files);
                  e.target.value = '';
                }}
              />
            </label>
          );
        })}
      </div>
      {rejected.length > 0 && (
        <p className="text-xs text-red-600">
          Too large (10MB max), not added: {rejected.join(', ')}
        </p>
      )}
      <p className="text-xs text-muted-foreground">Accepted: JPG, PNG, HEIC — Max 10MB per image</p>
    </div>
  );
}

// ─── Submission Confirmation Panel ────────────────────────────────────────────
function SubmissionConfirmation({ listingLabel, onReset }: { listingLabel: string; onReset: () => void }) {
  return (
    <div className="max-w-2xl mx-auto py-12 px-4 text-center">
      {/* Success icon */}
      <div className="flex items-center justify-center w-20 h-20 rounded-full bg-green-100 mx-auto mb-6">
        <Icon name="CheckCircleIcon" size={44} className="text-green-600" variant="solid" />
      </div>

      <h2 className="text-2xl font-extrabold text-foreground mb-2">Listing Submitted Successfully</h2>
      <p className="text-muted-foreground mb-8">
        Your <span className="font-semibold text-foreground">{listingLabel}</span> listing has been received and is now awaiting review.
      </p>

      {/* Review notice */}
      <div className="bg-amber-50 border border-amber-200 rounded-xl p-6 mb-8 text-left">
        <div className="flex items-start gap-4">
          <div className="shrink-0 mt-0.5">
            <Icon name="ClockIcon" size={28} className="text-amber-600" />
          </div>
          <div>
            <h3 className="text-base font-bold text-amber-900 mb-1">Under Administrator Review — 24 to 48 Hours</h3>
            <p className="text-sm text-amber-800 leading-relaxed">
              All listings on FarmTradeDirect are reviewed by our team before going live. This process typically takes
              <span className="font-semibold"> 48 hours</span> and helps maintain the quality and safety of our marketplace.
            </p>
            <ul className="mt-3 space-y-1.5">
              {[
                'You will be notified by email once your listing is approved and published.',
                'If any information needs clarification, our team will contact you directly.',
                'Listings that do not meet our guidelines may be declined or returned for amendment.',
              ].map((item, i) => (
                <li key={i} className="flex items-start gap-2 text-sm text-amber-800">
                  <Icon name="ChevronRightIcon" size={14} className="text-amber-500 mt-0.5 shrink-0" />
                  {item}
                </li>
              ))}
            </ul>
          </div>
        </div>
      </div>

      {/* What happens next */}
      <div className="grid grid-cols-1 sm:grid-cols-3 gap-4 mb-8 text-left">
        {[
          { icon: 'DocumentCheckIcon', title: 'Step 1 — Review', desc: 'Our team checks your listing for accuracy and compliance.' },
          { icon: 'BellAlertIcon',      title: 'Step 2 — Approval', desc: 'You receive an email confirmation once approved.' },
          { icon: 'GlobeAltIcon',       title: 'Step 3 — Live', desc: 'Your listing goes live and is visible to buyers across the UK.' },
        ].map(step => (
          <div key={step.title} className="bg-muted/40 rounded-xl p-4 flex flex-col gap-2">
            <Icon name={step.icon} size={22} className="text-primary" />
            <p className="text-sm font-bold text-foreground">{step.title}</p>
            <p className="text-xs text-muted-foreground leading-relaxed">{step.desc}</p>
          </div>
        ))}
      </div>

      <div className="flex flex-col sm:flex-row gap-3 justify-center">
        <button
          type="button"
          onClick={onReset}
          className="btn-primary px-8 py-3 text-sm font-bold"
        >
          <Icon name="PlusCircleIcon" size={18} />
          Post Another Listing
        </button>
        <a href="/marketplace-listings" className="btn-secondary px-8 py-3 text-sm font-bold flex items-center justify-center gap-2">
          <Icon name="MagnifyingGlassIcon" size={18} />
          Browse Marketplace
        </a>
      </div>
    </div>
  );
}

// ─── Form -> database mapping ─────────────────────────────────────────────────
// Field slugs that belong in a real `listings` column. Everything else the seller
// fills in is kept in `details` so nothing typed is ever thrown away.
const COLUMN_FOR_SLUG: Record<string, keyof NewListingInput> = {
  listing_title: 'title',
  job_title: 'title',
  breed: 'breed',
  breed_strain: 'breed',
  breed_if_applicable: 'breed',
  species_breed: 'breed',
  category: 'sub_category',
  type: 'sub_category',
  purpose: 'sub_category',
  number_of_head: 'quantity',
  number_of_pigs: 'quantity',
  number_of_goats: 'quantity',
  number_of_birds: 'quantity',
  number: 'quantity',
  quantity_volume: 'quantity',
  number_of_animals_load_size: 'quantity',
  age: 'age',
  age_age_range: 'age',
  age_weeks: 'age',
  assurance_scheme: 'assurance_scheme',
  price: 'price',
  price_type: 'price_type',
  budget_max_rate: 'price',
  town_village: 'location_town',
  collection_postcode_town: 'location_town',
  county: 'county',
  collection_county: 'county',
  description_additional_notes: 'description',
  additional_notes: 'description',
  special_requirements_notes: 'description',
  health_history_additional_notes: 'description',
  livestock_species: 'species',
  species: 'species',
  species_if_applicable: 'species',
};

const NUMERIC_COLUMNS = new Set<keyof NewListingInput>(['price', 'quantity']);

/** Turn the submitted form into a listings row plus a details blob. */
function buildListingFromForm(form: HTMLFormElement, type: ListingType): NewListingInput {
  const data = new FormData(form);
  const group = listingTypes.find((t) => t.type === type)?.group ?? 'livestock';

  const row: Record<string, unknown> = { category: CATEGORY_FOR_GROUP[group], species: type };
  const details: Record<string, string> = {};

  for (const [key, raw] of data.entries()) {
    if (typeof raw !== 'string') continue;
    const value = raw.trim();
    if (!value) continue;

    const column = COLUMN_FOR_SLUG[key];
    if (column) {
      if (NUMERIC_COLUMNS.has(column)) {
        const num = Number(value.replace(/[^0-9.-]/g, ''));
        if (!Number.isNaN(num)) row[column] = num;
      } else if (row[column] == null) {
        // First match wins, so a specific field can't be clobbered by a generic one.
        row[column] = value;
      }
    } else {
      details[key] = value;
    }
  }

  // The species column holds the listing type; a "Livestock Species" answer on a
  // haulage job is about the load, so keep it in details instead of overwriting.
  row.species = type;
  if (row.price_type === 'poa') row.price = null;
  row.details = details;

  return row as unknown as NewListingInput;
}

// ─── Main Screen ──────────────────────────────────────────────────────────────
export default function PostListingScreen() {
  const [activeType, setActiveType] = useState<ListingType>('cattle');
  const [submitted, setSubmitted] = useState(false);
  const [activeTab, setActiveTab] = useState<'sell' | 'buy'>('sell');
  const [photos, setPhotos] = useState<File[]>([]);
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [allowance, setAllowance] = useState<ListingAllowance | null>(null);
  const formRef = useRef<HTMLFormElement>(null);
  const { user, loading: authLoading } = useAuth();

  // Show the position before the form is filled in, not after it is refused.
  useEffect(() => {
    if (!user) {
      setAllowance(null);
      return;
    }
    let live = true;
    fetchListingAllowance(createClient())
      .then((a) => { if (live) setAllowance(a); })
      .catch(() => { /* the database still enforces it — this is only the label */ });
    return () => { live = false; };
  }, [user, submitted]);

  const outOfListings = allowance?.remaining === 0;

  const handleSubmit = async (e: React.FormEvent<HTMLFormElement>) => {
    e.preventDefault();
    const form = e.currentTarget;
    setError(null);

    if (!user) {
      setError('Please sign in or register before posting a listing — it only takes a minute.');
      window.scrollTo({ top: 0, behavior: 'smooth' });
      return;
    }

    setSaving(true);
    try {
      const supabase = createClient();
      const created = await createListing(supabase, buildListingFromForm(form, activeType), photos);
      // Nudge the admin team that something is waiting. Fire-and-forget — the
      // listing is saved, and the seller must never see a mail problem here.
      if (created?.id) void notifyListingSubmitted(created.id);
      form.reset();
      setPhotos([]);
      setSubmitted(true);
      window.scrollTo({ top: 0, behavior: 'smooth' });
    } catch (err) {
      setError(
        err instanceof Error
          ? err.message
          : 'Something went wrong saving your listing. Please try again.'
      );
      // If the refusal was the allowance, the banner should now agree with it.
      fetchListingAllowance(createClient()).then(setAllowance).catch(() => {});
      window.scrollTo({ top: 0, behavior: 'smooth' });
    } finally {
      setSaving(false);
    }
  };

  // One icon per group, from the same set the rest of the site uses. The types
  // themselves are a plain list: the old sidebar put a hotlinked stock photo and
  // a hand-drawn coloured animal next to every row, which read as clip art.
  const groups = [
    { key: 'livestock', label: 'Livestock',       Glyph: Beef,    types: listingTypes.filter(t => t.group === 'livestock') },
    { key: 'crops',     label: 'Arable & Forage', Glyph: Wheat,   types: listingTypes.filter(t => t.group === 'crops') },
    { key: 'machinery', label: 'Machinery',       Glyph: Tractor, types: listingTypes.filter(t => t.group === 'machinery') },
    { key: 'haulage',   label: 'Haulage',         Glyph: Truck,   types: listingTypes.filter(t => t.group === 'haulage') },
    { key: 'deadstock', label: 'Deadstock',       Glyph: Package, types: listingTypes.filter(t => t.group === 'deadstock') },
  ];

  const activeLabel = listingTypes.find(t => t.type === activeType)?.label ?? '';

  const renderForm = () => {
    switch (activeType) {
      case 'cattle':    return <CattleForm />;
      case 'sheep':     return <SheepForm />;
      case 'pigs':      return <PigsForm />;
      case 'horses':    return <HorsesForm />;
      case 'goats':     return <GoatsForm />;
      case 'poultry':   return <PoultryForm />;
      case 'deer':      return <DeerForm />;
      case 'alpacas':   return <AlpacasForm />;
      case 'grain':     return <GrainFeedForm />;
      case 'haulage':   return <HaulageForm />;
      case 'machinery': return <MachineryForm />;
      case 'deadstock': return <DeadstockForm />;
    }
  };

  if (submitted) {
    return (
      <main className="max-w-screen-xl mx-auto px-4 lg:px-8 py-8">
        <SubmissionConfirmation
          listingLabel={activeLabel}
          onReset={() => { setSubmitted(false); setActiveType('cattle'); }}
        />
      </main>
    );
  }

  return (
    <main className="max-w-screen-xl mx-auto px-4 lg:px-8 py-8">
      {/* Page header */}
      <div className="mb-6">
        <h1 className="text-2xl font-extrabold text-foreground">Post a Listing</h1>
        <p className="text-muted-foreground text-sm mt-1">
          Select your listing type below and complete the entry form. All fields marked <span className="text-red-500 font-bold">*</span> are required.
        </p>
      </div>

      {/* Tab toggle: Sell / Buy */}
      <div className="flex gap-2 mb-8 p-1 bg-muted rounded-xl w-full sm:w-auto sm:inline-flex">
        <button
          type="button"
          onClick={() => setActiveTab('sell')}
          className={`flex-1 sm:flex-none flex items-center justify-center gap-2 px-6 py-2.5 rounded-lg text-sm font-bold transition-all duration-150 ${
            activeTab === 'sell' ?'bg-primary text-primary-foreground shadow-sm' :'text-muted-foreground hover:text-foreground'
          }`}
        >
          <Icon name="TagIcon" size={16} />
          Post a Listing
        </button>
        <button
          type="button"
          onClick={() => setActiveTab('buy')}
          className={`flex-1 sm:flex-none flex items-center justify-center gap-2 px-6 py-2.5 rounded-lg text-sm font-bold transition-all duration-150 ${
            activeTab === 'buy' ?'bg-primary text-primary-foreground shadow-sm' :'text-muted-foreground hover:text-foreground'
          }`}
        >
          <Icon name="MagnifyingGlassIcon" size={16} />
          Post a Buyer Request
        </button>
      </div>

      {activeTab === 'buy' ? (
        <BuyerRequestSection />
      ) : (
      <div className="flex flex-col lg:flex-row gap-8">
        {/* Left: type selector */}
        <aside className="lg:w-60 xl:w-68 shrink-0">
          <div className="card p-3 sticky top-24">
            <p className="text-xs font-bold text-muted-foreground uppercase tracking-wider mb-3 px-1">Listing Type</p>
            {groups.map(group => (
              <div key={group.key} className="mb-4 last:mb-1">
                <div className="flex items-center gap-2 px-2 pb-1.5 mb-1 border-b border-border">
                  <group.Glyph className="w-3.5 h-3.5 text-primary shrink-0" strokeWidth={1.75} aria-hidden="true" />
                  <p className="text-[11px] font-bold text-muted-foreground uppercase tracking-wider">{group.label}</p>
                </div>
                {group.types.map(t => (
                  <button
                    key={t.type}
                    type="button"
                    onClick={() => setActiveType(t.type)}
                    aria-pressed={activeType === t.type}
                    className={`w-full flex items-center justify-between gap-2 px-2.5 py-2 rounded-lg text-sm text-left transition-colors duration-150 ${
                      activeType === t.type
                        ? 'bg-primary text-primary-foreground font-bold'
                        : 'text-foreground font-medium hover:bg-muted'
                    }`}
                  >
                    <span className="leading-tight truncate">{t.label}</span>
                    {activeType === t.type && (
                      <Icon name="CheckIcon" size={15} className="shrink-0" />
                    )}
                  </button>
                ))}
              </div>
            ))}
          </div>
        </aside>

        {/* Right: form */}
        <div className="flex-1 min-w-0">
          <form ref={formRef} onSubmit={handleSubmit} className="space-y-8">
            {/* Not signed in — posting needs an account, so say so up front */}
            {!authLoading && !user && (
              <div className="flex items-start gap-3 bg-blue-50 border border-blue-200 rounded-xl px-5 py-4">
                <Icon name="InformationCircleIcon" size={20} className="text-blue-600 shrink-0 mt-0.5" />
                <p className="text-sm text-blue-900">
                  <span className="font-bold">You&apos;re not signed in.</span> Fill in the form by all means, but
                  you&apos;ll need an account to submit it so buyers can reach you.{' '}
                  <a href="/sign-up-login-screen" className="underline font-semibold">Sign in or register</a>.
                </p>
              </div>
            )}

            {error && (
              <div className="flex items-start gap-3 bg-red-50 border border-red-200 rounded-xl px-5 py-4" role="alert">
                <Icon name="ExclamationTriangleIcon" size={20} className="text-red-600 shrink-0 mt-0.5" />
                <p className="text-sm text-red-800">{error}</p>
              </div>
            )}

            {/* Where the member stands on their plan's listing allowance. */}
            {allowance?.signedIn && (
              <div
                className={`flex items-start gap-3 rounded-xl px-5 py-4 border ${
                  outOfListings
                    ? 'bg-amber-50 border-amber-200'
                    : 'bg-slate-50 border-slate-200'
                }`}
              >
                <Icon
                  name={outOfListings ? 'ExclamationTriangleIcon' : 'InformationCircleIcon'}
                  size={20}
                  className={`shrink-0 mt-0.5 ${outOfListings ? 'text-amber-600' : 'text-slate-500'}`}
                />
                <p className={`text-sm ${outOfListings ? 'text-amber-900' : 'text-slate-700'}`}>
                  <span className="font-bold">{listingAllowanceLabel(allowance)}.</span>{' '}
                  {outOfListings ? (
                    <>
                      <a href="/membership-pricing" className="underline font-semibold">
                        Upgrade your plan
                      </a>{' '}
                      to post more, or wait until an older listing falls outside the 12 month window.
                    </>
                  ) : (
                    <>{listingLengthLabel(allowance.listingDays)}.</>
                  )}
                </p>
              </div>
            )}

            {/* Form card */}
            <div className="card">
              {renderForm()}
            </div>

            {/* Photos */}
            <div className="card">
              <PhotosSection photos={photos} onChange={setPhotos} />
            </div>

            {/* Location & Pricing */}
            <div className="card">
              <LocationPricingSection
                showHaulageFields={activeType === 'haulage'}
                bulkPricing={activeType === 'grain'}
              />
            </div>

            {/* Review notice above submit */}
            <div className="flex items-start gap-3 bg-amber-50 border border-amber-200 rounded-xl px-5 py-4">
              <Icon name="ClockIcon" size={20} className="text-amber-600 shrink-0 mt-0.5" />
              <p className="text-sm text-amber-800">
                <span className="font-bold">48 hour review period:</span> Every listing is reviewed and approved by hand by the FarmTradeDirect administration team before going live. You will be notified by email once your listing is published.
              </p>
            </div>

            {/* Terms & Submit */}
            <div className="card">
              <SectionHeading title="Terms & Submission" iconName="DocumentTextIcon" />
              <div className="space-y-3 mb-6">
                <label className="flex items-start gap-3 cursor-pointer">
                  <input type="checkbox" required className="mt-0.5 w-5 h-5 accent-primary shrink-0" />
                  <span className="text-sm text-foreground">
                    I confirm the information provided is accurate and I am authorised to sell / advertise these animals, machinery, or services.
                  </span>
                </label>
                <label className="flex items-start gap-3 cursor-pointer">
                  <input type="checkbox" required className="mt-0.5 w-5 h-5 accent-primary shrink-0" />
                  <span className="text-sm text-foreground">
                    I agree to the{' '}
                    {/* New tab deliberately — this form holds a lot of typed
                        state and navigating away would lose the lot. */}
                    <a href="/terms" target="_blank" rel="noopener noreferrer" className="text-primary underline">FarmTradeDirect Terms &amp; Conditions</a>
                    {' '}and{' '}
                    {/* TODO: there is no Listing Policy page — awaiting Alex's
                        decision on whether to write one or fold the listing
                        rules into section 5 of the Terms. */}
                    <a href="#" className="text-primary underline">Listing Policy</a>.
                  </span>
                </label>
                <label className="flex items-start gap-3 cursor-pointer">
                  <input type="checkbox" className="mt-0.5 w-5 h-5 accent-primary shrink-0" />
                  <span className="text-sm text-foreground">
                    I consent to FarmTradeDirect sharing my contact details with verified enquirers.
                  </span>
                </label>
              </div>
              <div className="flex flex-col sm:flex-row gap-3">
                <button
                  type="submit"
                  disabled={saving || outOfListings}
                  className="btn-primary flex-1 sm:flex-none sm:px-10 py-3 text-base font-bold disabled:opacity-60 disabled:cursor-not-allowed"
                >
                  <Icon name={saving ? 'ArrowPathIcon' : 'PaperAirplaneIcon'} size={20} className={saving ? 'animate-spin' : undefined} />
                  {saving
                    ? photos.length
                      ? `Uploading ${photos.length} photo${photos.length === 1 ? '' : 's'}…`
                      : 'Submitting…'
                    : outOfListings
                      ? 'Listing allowance used up'
                      : 'Submit Listing for Review'}
                </button>
                {/* "Save as Draft" used to sit here. It was a button with no
                    handler — clicking it did nothing at all, and there was no
                    drafts list to reopen one from. Gone rather than faked. */}
              </div>
              <p className="text-xs text-muted-foreground mt-3 flex items-center gap-1.5">
                <Icon name="ClockIcon" size={13} className="text-muted-foreground shrink-0" />
                Listings are reviewed within 48 hours. You will receive an email confirmation once your listing is approved and live.
              </p>
            </div>
          </form>
        </div>
      </div>
      )}
    </main>
  );
}
